import { useState } from 'react';
import DataTable from '../../../reusable/DataTable/DataTable';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import Button from '../../../reusable/Button/Button';
import Select from '../../../reusable/Select/Select';
import Input from '../../../reusable/Input/Input';
import TextArea from '../../../reusable/TextArea/TextArea';
import StatusFilterChips from '../StatusFilterChips';
import FilterDropdown from '../../../reusable/FilterDropdown/FilterDropdown';
import useAffiliateRequests, { formatBalance, requestStatusTone } from '../useAffiliateRequests';
import { USE_CASES } from './data';
import { t } from '../../../i18n/i18n';
import './PersonalAccount.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** `/affiliate/requests/options` limits, for when they haven't arrived yet. */
const DEFAULT_MAX_QUANTITY = 5;
const DEFAULT_PURPOSE_MAX = 120;

/** The dropdown offers what the chips do — they share one filter value. */
const STATUS_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Approved', value: 'Approved' },
  { label: 'Processing', value: 'Processing' },
  { label: 'Rejected', value: 'Rejected' },
];

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : DATE_FORMATTER.format(d);
};

/**
 * The accounts an approved request was issued — `issuance.issued_accounts`,
 * as login ids or as `{login_id}` rows. Nothing until the admin issues them.
 */
const issuedAccounts = (r) => {
  const issued = r.issuance?.issued_accounts ?? [];
  const ids = issued
    .map((a) => (a && typeof a === 'object' ? a.login_id ?? a.login ?? a.id : a))
    .filter(Boolean);
  return ids.length ? ids.join(', ') : '—';
};

const columns = [
  {
    key: 'accountSize',
    header: 'Account Size',
    width: 14,
    value: (r) => formatBalance(r.account_size ?? r.challenge?.account_balance),
  },
  { key: 'accountNumber', header: 'Account Number', width: 16, value: issuedAccounts },
  { key: 'quantity', header: 'Quantity', width: 10, value: (r) => r.quantity ?? '—' },
  { key: 'requestedOn', header: 'Requested', width: 14, value: (r) => formatDate(r.created_at) },
  { key: 'purpose', header: 'Purpose', width: 30, value: (r) => r.purpose || '—' },
  {
    key: 'status',
    header: 'Status',
    width: 16,
    render: (r) => (
      <StatusChip tone={requestStatusTone(r.status)}>{t(r.status || 'Processing')}</StatusChip>
    ),
  },
];

/**
 * Affiliate — Personal Account. Request a personal funded account, then
 * review past requests — 1:18840.
 * `/affiliate/requests/?request_type=personal_account`, through `useAffiliateRequests`.
 */
export default function PersonalAccount() {
  const {
    limits,
    challengeOptions,
    platformOptions,
    isOptionsLoading,
    eligibility,
    statusCounts,
    statusFilter,
    setStatusFilter,
    table,
    submit,
    isSubmitting,
  } = useAffiliateRequests('personal_account');

  const maxQuantity = limits.max_personal_account_quantity ?? DEFAULT_MAX_QUANTITY;
  const purposeMax = limits.purpose_max_length ?? DEFAULT_PURPOSE_MAX;

  const [challenge, setChallenge] = useState('');
  const [platform, setPlatform] = useState('');
  const [quantity, setQuantity] = useState('');
  const [purpose, setPurpose] = useState('');

  const chosenPlatform = platform || platformOptions[0]?.value || '';
  const notEligible = eligibility?.eligible === false;
  const qty = Number(quantity);

  const canSubmit = Boolean(
    !notEligible && challenge && chosenPlatform && qty >= 1 && qty <= maxQuantity && purpose.trim()
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    const ok = await submit(
      {
        challenge: Number(challenge),
        platform: chosenPlatform,
        quantity: qty,
        purpose: purpose.trim(),
      },
      t('Account request submitted')
    );
    if (ok) {
      setChallenge('');
      setQuantity('');
      setPurpose('');
    }
  };

  return (
    <div className="affiliate-personal-account">
      <div className="affiliate-personal-account__toolbar">
        <StatusFilterChips counts={statusCounts} value={statusFilter} onChange={setStatusFilter} />
      </div>

      <div className="affiliate-personal-account__body">
        <form className="account-form" onSubmit={handleSubmit}>
          <div className="account-form__head">
            <span className="icon account-form__icon" style={icon('affiliate-plus')} />
            <span className="account-form__title">{t('New Account Request')}</span>
          </div>
          <p className="account-form__subtitle">{t('Personal funded account · admin approval required')}</p>

          {notEligible && (
            <p className="account-form__footnote" role="alert">
              {eligibility.reason || t("Your account can't request personal accounts yet.")}
            </p>
          )}

          <Select
            label="Challenge"
            placeholder={isOptionsLoading ? t('Loading challenges...') : t('Select challenge')}
            value={challenge}
            onChange={(e) => setChallenge(e.target.value)}
            options={challengeOptions}
            required
            variant="form"
            disabled={notEligible}
          />

          <Select
            label="Platform"
            placeholder="Select platform"
            value={chosenPlatform}
            onChange={(e) => setPlatform(e.target.value)}
            options={platformOptions}
            required
            variant="form"
            disabled={notEligible}
          />

          <Input
            label="Quantity"
            type="number"
            min={1}
            max={maxQuantity}
            placeholder="Enter quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            hint={t('1 to {{max}} accounts · one open request at a time', { max: maxQuantity })}
            error={quantity && qty > maxQuantity ? t('At most {{max}} accounts per request', { max: maxQuantity }) : undefined}
            required
            variant="form"
            disabled={notEligible}
          />

          <TextArea
            label="Purpose"
            placeholder="How will you use this account ..."
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            maxLength={purposeMax}
            required
            variant="form"
            disabled={notEligible}
          />

          <div className="account-usecases">
            {USE_CASES.map((c) => (
              <button
                key={c}
                type="button"
                className={`account-usecase${purpose === c ? ' is-active' : ''}`}
                onClick={() => setPurpose(c)}
                disabled={notEligible}
              >
                {t(c)}
              </button>
            ))}
          </div>

          <Button
            variant="pill"
            className="account-submit"
            height={28.8}
            paddingInline={16}
            htmlType="submit"
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
          >
            {t('Submit Request')}
          </Button>

          <p className="account-form__footnote">
            {t('Approved accounts appear in your history with their account number.')}
          </p>
        </form>

        <DataTable
          className="affiliate-personal-account__table"
          icon="affiliate-recent-requests"
          title="Request History"
          count={`${table.total}`}
          columns={columns}
          rows={table.rows}
          getRowId={(r) => r.id}
          total={table.total}
          page={table.page}
          pageSize={table.pageSize}
          onPageChange={table.onPageChange}
          onSearch={table.onSearch}
          loading={table.loading}
          emptyMessage={table.emptyMessage}
          searchPlaceholder="Search requests"
          minWidth={540}
          actions={
            <FilterDropdown
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              label="Filters"
            />
          }
        />
      </div>
    </div>
  );
}
